import i18n from "@/i18n/index";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { printPdf } from "@/utils/printPdf";
import { currentClub } from "@/shared/state";
import { getInvoiceConfig } from "@/config/invoiceRegistry";
import { useGlobal } from "@/composables/global/useGlobal";

// Extend jsPDF with autoTable
(jsPDF as any).API.autoTable = autoTable;

/** Standard 80mm ticket dimensions */
export const TICKET_WIDTH = 80;
export const TICKET_HEIGHT = 200;

/** Standard spacing and layout (mm) */
export const TICKET_START_Y = 5;
export const TICKET_MARGIN = 5;
export const TICKET_SPACING_SMALL = 3;
export const TICKET_SPACING_MEDIUM = 5;
export const TICKET_SPACING_AFTER_LINE = 5;
export const TICKET_SPACING_BEFORE_LINE = 5;

/** Default values for ticket header */
const DEFAULT_ADDRESS = "Loh HIND N23, EL JADIDA";
const DEFAULT_PHONE = "+2126 33 40 22 41";
const DEFAULT_THANKS = "Merci !";

export interface TicketHeaderOptions {
  /** Club name (defaults to currentClub?.name) */
  clubName?: string;
  /** Address line (defaults to invoice config or default) */
  address?: string;
  /** Phone number (defaults to invoice config or default) */
  phone?: string;
  /** Skip address line */
  skipAddress?: boolean;
  /** Skip phone line */
  skipPhone?: boolean;
}

/**
 * Column width ratios for ticket tables (must sum to 1).
 * Example: [0.5, 0.25, 0.25] = 50%, 25%, 25%
 */
export type TicketTableColumnRatios = number[];

export interface TicketTableOptions {
  /** Table headers (one per column) */
  head: string[];
  /** Table body rows */
  body: string[][];
  /** Column width ratios (0-1). If omitted, uses equal widths. */
  columnRatios?: TicketTableColumnRatios;
}

export interface Ticket80mmContext {
  doc: jsPDF;
  startY: number;
  pageWidth: number;
  pageHeight: number;
  marginLeft: number;
  marginRight: number;
  usableWidth: number;
  X_POS_CENTER: number;
  START_COL_X: number;
  addDashedLine: () => void;
  addDashedLineWithSpacing: () => void;
  addTicketHeader: (options?: TicketHeaderOptions) => void;
  addTicketTitle: (title: string) => void;
  addTicketTable: (options: TicketTableOptions) => void;
  addTicketFooter: (thanksMessage?: string) => void;
  addNewHeight: () => void;
  addPage: (orientation?: "p" | "l") => void;
}
  const {  translateToFrench } = useGlobal();
export interface TicketOutputOptions {
  typeOfFile: "print" | "excel" | "pdf" | "mail"  ;
  fileName?: string;
}

/**
 * Creates a new 80mm ticket PDF document with standard layout and helpers.
 * All ticket generators should use this for consistent structure.
 */
export function createTicket80mm(): Ticket80mmContext {
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    putOnlyUsedFonts: true,
    precision: 20,
    format: [TICKET_WIDTH, TICKET_HEIGHT],
  });

  const pageWidth =
    doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = TICKET_MARGIN;
  const marginRight = TICKET_MARGIN;
  const usableWidth = pageWidth - marginLeft - marginRight;
  const X_POS_CENTER = pageWidth / 2;
  const START_COL_X = TICKET_MARGIN;

  const state = { startY: TICKET_START_Y };

  const addDashedLine = () => {
    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(6);
    const dashWidth = doc.getTextWidth("-");
    const totalDashes = Math.floor(usableWidth / dashWidth);
    const dashedLine = "-".repeat(totalDashes);
    doc.text(dashedLine, marginLeft, state.startY);
  };

  /** Adds standard spacing, dashed line, then spacing. Use for section separators. */
  const addDashedLineWithSpacing = () => {
    state.startY += TICKET_SPACING_BEFORE_LINE;
    addDashedLine();
    state.startY += TICKET_SPACING_AFTER_LINE;
    addNewHeight();
  };

  const addTicketHeader = (options?: TicketHeaderOptions) => {
    const club = currentClub?.value ?? null;
    console.log("club");
    console.log(club);
    const clubName = options?.clubName ?? club?.name ?? "";
    const city =   club?.city ?? "";
    const address = (options?.address ?? club?.address ?? "") + (city ? ", " + city : '');
    const phone = options?.phone ?? club?.phone ?? "";

    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(12);
    doc.text(clubName, X_POS_CENTER, state.startY, { align: "center" });
    state.startY += 4;

    if (!options?.skipAddress) {
      doc.setFont("helvetica", "normal", "bold");
      doc.setFontSize(10);
      doc.text(address , X_POS_CENTER, state.startY, { align: "center" });
      state.startY += 4;
    }

    if (!options?.skipPhone) {
      doc.setFont("helvetica", "normal", "bold");
      doc.setFontSize(10);
      doc.text(phone, X_POS_CENTER, state.startY, { align: "center" });
    }

    addDashedLineWithSpacing();
  };

  const addTicketTitle = (title: string) => {
    doc.setFont("helvetica", "normal", "bold");
    doc.setFontSize(10);
    doc.text(title, X_POS_CENTER, state.startY, { align: "center" });
    state.startY += TICKET_SPACING_MEDIUM;
    addDashedLine();
    state.startY += TICKET_SPACING_AFTER_LINE;
  };

  /**
   * Adds a table with fixed 80mm ticket styling.
   * Only head and body content are dynamic; layout, fonts, and styles are shared.
   */
  const addTicketTable = (options: TicketTableOptions) => {
    const { head, body, columnRatios } = options;
    const colCount = head.length;
    const ratios =
      columnRatios && columnRatios.length === colCount
        ? columnRatios
        : Array(colCount).fill(1 / colCount);

    const columnStyles: Record<number, { cellWidth: number }> = {};
    ratios.forEach((ratio, i) => {
      columnStyles[i] = { cellWidth: usableWidth * ratio };
    });

    autoTable(doc, {
      margin: { top: 0, left: marginLeft, right: marginRight },
      startY: state.startY,
      pageBreak: "auto",
      theme: "plain",
      head: [head],
      body,
      foot: [],
      headStyles: {
        fontSize: 8.5,
        font: "helvetica",
        cellPadding: 1,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8.5,
        font: "helvetica",
        cellPadding: 1,
      },
      columnStyles,
      styles: {
        font: "helvetica",
      },
    });

    state.startY = (doc as any).lastAutoTable?.finalY ?? state.startY;
  };

  const addTicketFooter = (thanksMessage?: string) => {
    addDashedLineWithSpacing();
    doc.setFont("helvetica", "normal", "normal");
    doc.setFontSize(7);
    doc.text(
      thanksMessage ??
        translateToFrench("tables.subscription_member.tickets.thanks") ??
        DEFAULT_THANKS,
      X_POS_CENTER,
      state.startY,
      { align: "center" },
    );
  };

  const addNewHeight = () => {
    if (state.startY + 10 > pageHeight) {
      doc.addPage([TICKET_WIDTH, TICKET_HEIGHT]);
      state.startY = TICKET_START_Y;
    }
  };

  const addPage = (orientation: "p" | "l" = "p") => {
    doc.addPage([TICKET_WIDTH, TICKET_HEIGHT], orientation);
    state.startY = TICKET_START_Y;
  };

  return {
    get doc() {
      return doc;
    },
    get startY() {
      return state.startY;
    },
    set startY(value: number) {
      state.startY = value;
    },
    pageWidth,
    pageHeight,
    marginLeft,
    marginRight,
    usableWidth,
    X_POS_CENTER,
    START_COL_X,
    addDashedLine,
    addDashedLineWithSpacing,
    addTicketHeader,
    addTicketTitle,
    addTicketTable,
    addTicketFooter,
    addNewHeight,
    addPage,
  };
}

/**
 * Outputs the ticket PDF: print, save, or prepare for mail.
 */
export async function outputTicket80mm(
  doc: jsPDF,
  options: TicketOutputOptions,
): Promise<void> {
  const { typeOfFile, fileName } = options;

  if (typeOfFile === "print") {
    await printPdf(doc);
  } else if (typeOfFile === "pdf" || typeOfFile === "mail") {
    doc.save(fileName ?? `Ticket_${Date.now()}.pdf`);
  }
}
